/**
 * Analytics service — GA4 e-commerce event tracking via GTM dataLayer.
 *
 * All events flow through `window.dataLayer` to the GTM container, which owns
 * the GA4 tag firing. Never call gtag() directly for e-commerce events.
 *
 * Rules enforced here:
 *  - `ecommerce: null` is pushed before every e-commerce event to prevent
 *    stale item arrays bleeding into subsequent events (GA4 spec).
 *  - All monetary values are JS numbers, never strings.
 *  - No `any` types.
 */

import { jwtDecode } from 'jwt-decode';
import type { JwtPayload } from '@/lib/types/jwt';
import type { ProductDetails, ProductOverview } from '@/lib/types/product';
import type { ShoppingCartItem } from '@/lib/types/cart';
import type { OrderItem } from '@/lib/types/order';
import type {
  Ga4Item,
  Ga4EcommerceEvent,
  SearchEventPayload,
  ViewItemListEvent,
  SelectItemEvent,
  ViewItemEvent,
  AddToCartEvent,
  ViewCartEvent,
  RemoveFromCartEvent,
  BeginCheckoutEvent,
  AddShippingInfoEvent,
  AddPaymentInfoEvent,
  PurchaseEvent,
} from './types';

// ─── Core push ───────────────────────────────────────────────────────────────

/** Push a GA4 e-commerce event to the GTM dataLayer, clearing the previous
 *  ecommerce payload first (required by GA4 to avoid stale item arrays). */
export function pushEcommerceEvent(event: Ga4EcommerceEvent): void {
  window.dataLayer.push({ ecommerce: null });
  window.dataLayer.push(event);
}

// ─── Identity ────────────────────────────────────────────────────────────────

/** Decode the nopCommerce JWT, SHA-256 hash the `CustomerId` claim, and push
 *  `user_id` to the dataLayer.  GA4 uses this for cross-device user stitching.
 *  Clears `user_id` on logout (token === null). */
export async function setUserId(token: string | null): Promise<void> {
  if (!token) {
    window.dataLayer.push({ user_id: undefined });
    return;
  }
  try {
    const decoded = jwtDecode<JwtPayload>(token);
    const customerId = decoded.CustomerId;
    if (!customerId) return;
    const data = new TextEncoder().encode(customerId);
    const hashBuffer = await window.crypto.subtle.digest('SHA-256', data);
    const hashHex = Array.from(new Uint8Array(hashBuffer))
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('');
    window.dataLayer.push({ user_id: hashHex });
  } catch {
    // Never block analytics flow for auth errors.
  }
}

// ─── Item mappers ────────────────────────────────────────────────────────────

/** Map a catalog `ProductOverview` (list page) to a GA4 item.
 *  Does not include brand/category — those require the full detail model. */
export function toGa4ItemFromProductOverview(
  product: ProductOverview,
  overrides?: Partial<Ga4Item>,
): Ga4Item {
  return {
    item_id: product.sku ?? String(product.id),
    item_name: product.name ?? '',
    price: product.productPrice?.priceValue ?? 0,
    quantity: 1,
    ...overrides,
  };
}

/** Map a `ProductDetails` (detail page) to a GA4 item.
 *  Includes brand from the first manufacturer and up to 3 category levels. */
export function toGa4ItemFromProductDetails(
  product: ProductDetails,
  overrides?: Partial<Ga4Item>,
): Ga4Item {
  const manufacturers = product.productManufacturers;
  const breadcrumb = product.breadcrumb?.categoryBreadcrumb ?? [];
  return {
    item_id: product.sku ?? String(product.id),
    item_name: product.name ?? '',
    item_brand: manufacturers?.[0]?.name ?? undefined,
    item_category: breadcrumb[0]?.name ?? undefined,
    item_category2: breadcrumb[1]?.name ?? undefined,
    item_category3: breadcrumb[2]?.name ?? undefined,
    price: product.productPrice?.priceValue ?? 0,
    quantity: 1,
    ...overrides,
  };
}

/** Map a `ShoppingCartItem` to a GA4 item. */
export function toGa4ItemFromCartItem(item: ShoppingCartItem): Ga4Item {
  return {
    item_id: item.sku ?? String(item.productId),
    item_name: item.productName ?? '',
    price: item.unitPriceValue,
    quantity: item.quantity,
    discount: item.discountValue > 0 ? item.discountValue : undefined,
  };
}

/** Map an `OrderItem` (from the confirmation page) to a GA4 item. */
export function toGa4ItemFromOrderItem(item: OrderItem): Ga4Item {
  return {
    item_id: item.Sku ?? String(item.ProductId),
    item_name: item.ProductName ?? '',
    price: item.UnitPriceInclTaxValue,
    quantity: item.Quantity,
  };
}

// ─── Typed track functions ───────────────────────────────────────────────────

export function trackViewItemList(
  payload: ViewItemListEvent['ecommerce'],
): void {
  pushEcommerceEvent({ event: 'view_item_list', ecommerce: payload });
}

export function trackSelectItem(
  payload: SelectItemEvent['ecommerce'],
): void {
  pushEcommerceEvent({ event: 'select_item', ecommerce: payload });
}

export function trackViewItem(payload: ViewItemEvent['ecommerce']): void {
  pushEcommerceEvent({ event: 'view_item', ecommerce: payload });
}

export function trackSearch(term: string): void {
  const searchEvent: SearchEventPayload = { event: 'search', search_term: term };
  window.dataLayer.push(searchEvent);
}

export function trackAddToCart(payload: AddToCartEvent['ecommerce']): void {
  pushEcommerceEvent({ event: 'add_to_cart', ecommerce: payload });
}

export function trackViewCart(payload: ViewCartEvent['ecommerce']): void {
  pushEcommerceEvent({ event: 'view_cart', ecommerce: payload });
}

export function trackRemoveFromCart(
  payload: RemoveFromCartEvent['ecommerce'],
): void {
  pushEcommerceEvent({ event: 'remove_from_cart', ecommerce: payload });
}

export function trackBeginCheckout(
  payload: BeginCheckoutEvent['ecommerce'],
  checkoutStepName: string,
): void {
  pushEcommerceEvent({
    event: 'begin_checkout',
    ecommerce: payload,
    checkout_step_name: checkoutStepName,
  });
}

export function trackAddShippingInfo(
  payload: AddShippingInfoEvent['ecommerce'],
  checkoutStepName: string,
): void {
  pushEcommerceEvent({
    event: 'add_shipping_info',
    ecommerce: payload,
    checkout_step_name: checkoutStepName,
  });
}

export function trackAddPaymentInfo(
  payload: AddPaymentInfoEvent['ecommerce'],
  checkoutStepName: string,
): void {
  pushEcommerceEvent({
    event: 'add_payment_info',
    ecommerce: payload,
    checkout_step_name: checkoutStepName,
  });
}

export function trackPurchase(payload: PurchaseEvent['ecommerce']): void {
  pushEcommerceEvent({ event: 'purchase', ecommerce: payload });
}
